// timeTrackingService.js
// API service for time tracking (check-in/check-out) functionality

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Technician Endpoints
// ============================================================================

/**
 * Get current check-in status for a location
 * @param {number} locationId - The installer location ID
 * @param {number} userId - The user ID to check status for
 * @returns {Object} { checked_in, session, user_id, location_id }
 */
export const getStatus = async (locationId, userId) => {
    const { data } = await API.get(`/api/time/status/${locationId}?user_id=${userId}`);
    return data.data || data;
};

/**
 * Check in at a location
 * @param {Object} checkInData - { location_id, user_id, lat, lng, accuracy_m, device_info? }
 * @returns {Object} { success, session_id, check_in_time }
 */
export const checkIn = async (checkInData) => {
    const { data } = await API.post('/api/time/checkin', checkInData);
    return data;
};

/**
 * Check out from current session
 * @param {Object} checkOutData - { location_id, user_id, lat, lng, accuracy_m, device_info? }
 * @returns {Object} { success, session_id, check_out_time, duration_minutes }
 */
export const checkOut = async (checkOutData) => {
    const { data } = await API.post('/api/time/checkout', checkOutData);
    return data;
};

// ============================================================================
// Manager Endpoints
// ============================================================================

/**
 * Get time tracking status for all staff at an installer
 * @param {number} installerId - The installer ID
 * @returns {Array} Array of staff with time status
 */
export const getStaffTimeStatus = async (installerId) => {
    const { data } = await API.get(`/api/time/staff-status/${installerId}`);
    return data.data || data;
};

/**
 * Manager check-in on behalf of a technician
 * @param {number} technicianId - The technician's user ID
 * @param {number} locationId - The location ID
 * @param {string} reasonCode - Reason code (forgot_phone, app_issues, new_employee, other)
 * @param {string} note - Optional note for the check-in
 * @param {number} actorUserId - The manager's user ID performing the action
 * @returns {Object} { success, session_id }
 */
export const managerCheckIn = async (technicianId, locationId, reasonCode, note = '', actorUserId) => {
    const { data } = await API.post('/api/time/manager/checkin', {
        technician_id: technicianId,
        location_id: locationId,
        reason_code: reasonCode,
        note: note,
        actor_user_id: actorUserId
    });
    return data;
};

/**
 * Manager check-out on behalf of a technician
 * @param {number} technicianId - The technician's user ID
 * @param {number} locationId - The location ID
 * @param {string} reasonCode - Optional reason code
 * @param {string} note - Optional note for the check-out
 * @param {number} actorUserId - The manager's user ID performing the action
 * @returns {Object} { success, session_id, duration_minutes }
 */
export const managerCheckOut = async (technicianId, locationId, reasonCode = '', note = '', actorUserId) => {
    const { data } = await API.post('/api/time/manager/checkout', {
        technician_id: technicianId,
        location_id: locationId,
        reason_code: reasonCode,
        note: note,
        actor_user_id: actorUserId
    });
    return data;
};

/**
 * Get all sessions for a location (with optional date filters)
 * @param {number} locationId - The installer location ID
 * @param {Object} options - { start_date?, end_date?, user_id?, status? }
 * @returns {Array} Array of session objects
 */
export const getSessionsByLocation = async (locationId, options = {}) => {
    const { data } = await API.get(`/api/time/sessions/${locationId}`, options);
    return data.data || data;
};

/**
 * Get all sessions for a specific user
 * @param {number} userId - The user ID
 * @param {Object} options - { start_date?, end_date?, location_id? }
 * @returns {Array} Array of session objects
 */
export const getSessionsByUser = async (userId, options = {}) => {
    const { data } = await API.get(`/api/time/sessions/user/${userId}`, options);
    return data.data || data;
};

/**
 * Get events (check-in, check-out, edits) for a specific session
 * @param {number} sessionId - The session ID
 * @returns {Array} Array of event objects with timestamps and details
 */
export const getSessionEvents = async (sessionId) => {
    const { data } = await API.get(`/api/time/events/${sessionId}`);
    return data.data || data;
};

/**
 * Edit a session (adjust times, add notes)
 * @param {number} sessionId - The session ID
 * @param {string} checkInAt - New check-in time (ISO string)
 * @param {string} checkOutAt - New check-out time (ISO string, optional)
 * @param {string} reasonCode - Reason code (time_correction, missed_punch, system_error, other)
 * @param {string} note - Note explaining the edit
 * @param {number} actorUserId - The manager's user ID performing the action
 * @returns {Object} { success, session }
 */
export const editSession = async (sessionId, checkInAt, checkOutAt, reasonCode, note, actorUserId) => {
    const { data } = await API.put(`/api/time/sessions/${sessionId}`, {
        check_in_at: checkInAt,
        check_out_at: checkOutAt,
        reason_code: reasonCode,
        note: note,
        actor_user_id: actorUserId
    });
    return data;
};

/**
 * Force close an open session (e.g., forgot to check out)
 * @param {number} sessionId - The session ID
 * @param {string} reasonCode - Reason code for closing
 * @param {string} note - Note explaining the force close
 * @param {number} actorUserId - The manager's user ID performing the action
 * @returns {Object} { success, session }
 */
export const forceCloseSession = async (sessionId, reasonCode, note = '', actorUserId) => {
    const { data } = await API.post(`/api/time/sessions/${sessionId}/close`, {
        reason_code: reasonCode,
        note: note,
        actor_user_id: actorUserId
    });
    return data;
};

// ============================================================================
// Admin Endpoints
// ============================================================================

/**
 * Update time tracking settings for a location
 * @param {number} locationId - The installer location ID
 * @param {Object} settings - { geofence_enabled?, geofence_radius_ft?, auto_checkout_hours?, require_photo? }
 * @param {number} actorUserId - The admin's user ID performing the action
 * @returns {Object} { success, settings }
 */
export const updateTimeSettings = async (locationId, settings, actorUserId) => {
    const { data } = await API.put(`/api/installer-location/${locationId}/time-settings`, {
        ...settings,
        actor_user_id: actorUserId
    });
    return data;
};

/**
 * Get audit log of time settings changes
 * @param {number} locationId - The installer location ID
 * @returns {Array} Array of audit entries
 */
export const getTimeSettingsAudit = async (locationId) => {
    const { data } = await API.get(`/api/installer-location/${locationId}/time-settings/audit`);
    return data.data || data;
};

// ============================================================================
// Reporting Endpoints
// ============================================================================

/**
 * Get timesheet report - hours by technician
 * @param {number} locationId - The installer location ID
 * @param {Object} filters - { startDate?, endDate?, technicianId?, daily? }
 * @returns {Object} { success, data: [...] }
 */
export const getTimesheetReport = async (locationId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.technicianId) params.append('technicianId', filters.technicianId);
    if (filters.daily) params.append('daily', 'true');

    const { data } = await API.get(`/api/time/reports/timesheet/${locationId}?${params}`);
    return data;
};

/**
 * Get compliance report - self vs manager check-in rates
 * @param {number} locationId - The installer location ID
 * @param {Object} filters - { startDate?, endDate? }
 * @returns {Object} { success, summary: {...}, by_technician: [...] }
 */
export const getComplianceReport = async (locationId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const { data } = await API.get(`/api/time/reports/compliance/${locationId}?${params}`);
    return data;
};

/**
 * Get exception report - flagged and auto-closed sessions
 * @param {number} locationId - The installer location ID
 * @param {Object} filters - { startDate?, endDate?, status? }
 * @returns {Object} { success, summary: {...}, data: [...] }
 */
export const getExceptionReport = async (locationId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.status) params.append('status', filters.status);

    const { data } = await API.get(`/api/time/reports/exceptions/${locationId}?${params}`);
    return data;
};

/**
 * Download time tracking data as CSV export
 * @param {number} locationId - The installer location ID
 * @param {Object} filters - { startDate?, endDate?, technicianId?, status? }
 * @returns {Promise} Resolves when download completes
 */
export const downloadTimeExport = async (locationId, filters = {}) => {
    const params = new URLSearchParams();
    params.append('format', 'csv');
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.technicianId) params.append('technicianId', filters.technicianId);
    if (filters.status) params.append('status', filters.status);

    // Fetch CSV data through authenticated API
    const { data } = await API.get(`/api/time/reports/export/${locationId}?${params}`, {
        responseType: 'blob'
    });

    // Create download link
    const blob = new Blob([data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `timesheet-export-${filters.startDate || 'all'}-to-${filters.endDate || 'all'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Get time sessions with summary stats for a specific user
 * @param {number} userId - The user ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Object} { summary: { total_hours, session_count, days_worked }, sessions: [...] }
 */
export const getUserTimeSummary = async (userId, startDate, endDate) => {
    const { data } = await API.get(`/api/time/sessions/user/${userId}/summary`, {
        start_date: startDate,
        end_date: endDate
    });
    return data;
};

// ============================================================================
// Default Export
// ============================================================================

const TimeTrackingAPI = {
    // Technician
    getStatus,
    checkIn,
    checkOut,
    // Manager
    getStaffTimeStatus,
    managerCheckIn,
    managerCheckOut,
    getSessionsByLocation,
    getSessionsByUser,
    getUserTimeSummary,
    getSessionEvents,
    editSession,
    forceCloseSession,
    // Admin
    updateTimeSettings,
    getTimeSettingsAudit,
    // Reporting
    getTimesheetReport,
    getComplianceReport,
    getExceptionReport,
    downloadTimeExport
};

export default TimeTrackingAPI;
